import { Badge, DateText } from '@/components';
import { QueryStates, RunnerPage } from '@/features/runner-monitor/RunnerPage';
import { BKK, duration, useNow } from '@/features/runner-monitor/format';
import { useRunnerOverview } from '@/features/runner-monitor/hooks';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';

const Tile = (props: { label: string; href: string; children: JSX.Element; sub?: JSX.Element }) => (
  <A
    href={props.href}
    class="block rounded-xl border border-neutral-200 bg-surface p-5 shadow-sm transition-shadow duration-200 ease-out hover:shadow-md motion-reduce:transition-none dark:border-neutral-800 dark:bg-surface-dark"
  >
    <div class="text-sm text-neutral-500">{props.label}</div>
    <div class="mt-1 text-3xl font-semibold tabular-nums">{props.children}</div>
    <Show when={props.sub}>
      <div class="mt-1 text-xs text-neutral-500">{props.sub}</div>
    </Show>
  </A>
);

const Split = (props: { parts: [string, number, string][] }) => (
  <span class="flex flex-wrap items-baseline gap-x-3">
    <For each={props.parts}>
      {([label, n, color]) => (
        <span>
          <span class={color}>{n}</span>
          <span class="ml-1 text-sm font-normal text-neutral-500">{label}</span>
        </span>
      )}
    </For>
  </span>
);

export default function RunnersOverview() {
  const q = useRunnerOverview();
  const now = useNow(1000);
  const o = () => q.data?.data;
  /** The server's figure ticks on locally until the next refetch. */
  const longestWait = () => {
    const s = o()?.longestQueuedSeconds;
    return s === null || s === undefined ? null : s + Math.max(0, now() - q.dataUpdatedAt) / 1000;
  };

  return (
    <RunnerPage
      title="CI Runners"
      sub="Self-hosted GitHub Actions runners"
      updatedAt={q.dataUpdatedAt}
    >
      <QueryStates pending={q.isPending} error={q.error} empty={false} emptyTitle="">
        <Show when={o()}>
          {(data) => (
            <>
              <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Tile label="Machines" href="/runners/machines">
                  <Split
                    parts={[
                      ['online', data().machines.online, 'text-emerald-600'],
                      [
                        'offline',
                        data().machines.offline,
                        data().machines.offline ? 'text-red-600' : '',
                      ],
                    ]}
                  />
                </Tile>
                <Tile label="Runners" href="/runners/runners">
                  <Split
                    parts={[
                      ['busy', data().runners.busy, 'text-brand-600'],
                      ['idle', data().runners.idle, 'text-emerald-600'],
                      ['offline', data().runners.offline, ''],
                    ]}
                  />
                </Tile>
                <Tile label="Jobs" href="/runners/queue">
                  <Split
                    parts={[
                      ['running', data().jobs.running, 'text-brand-600'],
                      ['queued', data().jobs.queued, data().jobs.queued ? 'text-amber-600' : ''],
                    ]}
                  />
                </Tile>
                <Tile
                  label="Longest wait for a runner"
                  href="/runners/queue"
                  sub="Queued jobs only — approvals and dependencies excluded"
                >
                  {longestWait() === null ? '—' : duration(longestWait())}
                </Tile>
              </div>

              <div class="rounded-xl border border-neutral-200 bg-surface p-4 text-sm dark:border-neutral-800 dark:bg-surface-dark">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="font-medium">GitHub</span>
                  <Show
                    when={data().github.configured}
                    fallback={<Badge tone="warning">Not connected</Badge>}
                  >
                    <Show
                      when={!data().github.rateLimitedUntil}
                      fallback={<Badge tone="warning">Rate limited</Badge>}
                    >
                      <Badge tone={data().github.lastError ? 'warning' : 'success'}>
                        {data().github.lastError ? 'Partial' : 'Syncing'}
                      </Badge>
                    </Show>
                  </Show>
                  <span class="text-neutral-500">
                    <Show
                      when={data().github.lastSyncAt}
                      fallback="no sync yet since the API started"
                    >
                      {(at) => (
                        <>
                          last sync{' '}
                          <DateText value={at()} fallbackTimeZone={BKK.fallbackTimeZone} />
                        </>
                      )}
                    </Show>
                  </span>
                </div>
                <Show when={data().github.rateLimitedUntil}>
                  {(until) => (
                    <p class="mt-2 text-amber-700 dark:text-amber-300">
                      Paused until{' '}
                      <DateText value={until()} fallbackTimeZone={BKK.fallbackTimeZone} />; runner
                      and queue data may be stale.
                    </p>
                  )}
                </Show>
                <Show when={data().github.lastError && !data().github.rateLimitedUntil}>
                  <p class="mt-2 break-words text-xs text-neutral-500">{data().github.lastError}</p>
                </Show>
                <Show when={!data().github.configured}>
                  <p class="mt-2 text-xs text-neutral-500">
                    Runner, queue and history data come from the GitHub App — see
                    docs/spec/0013-integration-setup.md. Machine metrics still work.
                  </p>
                </Show>
              </div>
            </>
          )}
        </Show>
      </QueryStates>
    </RunnerPage>
  );
}
